import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { withPropsOnChange } from 'recompose';
import { formatUSD } from '@sparefoot/react-core/utils/format';
import { facilityUrl } from 'utils/routes/url';

import { Card } from 'components/legacy/Card';
import { Link } from 'components/core/Link';
import { Group } from 'components/core/Page';
import { H4, Paragraph } from 'components/core/Typography';
import { FacilityImage } from 'components/facility/FacilityImage';
import { UnitIcon } from 'components/unit/UnitIcon';

import './BestPricedUnits.scss';

const sizeBuckets = ['5x5', '10x10', '10x15', '10x20'];

export const enhance = withPropsOnChange(
	['pricingStats'], ({ pricingStats, location }) => ({
		units: sizeBuckets.reduce((units, size) => {
			const lowestPricedUnit = get(pricingStats, ['lowestPrices', size]);
			const averagePrice = get(pricingStats, ['averagePrices', size, 'price']);

			if (get(lowestPricedUnit, 'price', null) && averagePrice) {
				return [
					...units,
					{
						...lowestPricedUnit,
						sizeText: size,
						averagePrice,
						reserveUrl: facilityUrl({
							seoLocation: location,
							id: lowestPricedUnit.facility,
							name: lowestPricedUnit.facilityName,
							inNetwork: get(lowestPricedUnit, 'inNetwork', false)
						})
					}
				];
			}

			return units;
		}, [])
	})
);

export function BestPricedUnits({
	location, units
}) {
	if (!units) return null;
	return (
		<Card
			header={`Best Priced Storage Units in ${location.city}, ${location.state}`}
			className="best-priced-units"
			segmentCategory="best priced storage in city widget interactions"
		>
			<div className="card-content">
				<H4 className="card-title">
					Find the best storage unit price in {location.city}, {location.state}
				</H4>
				<Paragraph>
					A storage unit’s price is based on several factors, including
					the facility’s amenities (such as 24/7 access, free use of truck),
					the storage unit’s amenities (such as climate control, electrical
					outlet), facility location, unit location (a ground floor unit
					will be more expensive), and overall demand in your area.
				</Paragraph>
				<Paragraph>
					When looking at pricing for storage units in {location.city}, it’s
					important to think about rental rates in terms of price for value.
					Sometimes, the cheapest storage unit doesn’t always mean it’s the
					best unit for YOUR needs.
				</Paragraph>
				{!!units.length &&
					<ul className="best-unit-list">
						{units.map((unit) => (
							<li
								key={unit.unit}
								className="best-unit-item"
							>
								<Group>
									<Link
										className="best-facility-link"
										segmentLabel="best priced text link"
										segmentProperties={{
											value: `best-priced ${location.city}, ${location.state} ${unit.sizeText} Unit`
										}}
										to={unit.reserveUrl}
									>
										Best-Priced {location.city}, {location.state} {unit.sizeText} Unit
									</Link>
									<Paragraph className="unit-text">
										If you're looking for a cheap <strong>{unit.sizeText}</strong> unit
										in <strong>{location.city}</strong>, you'll get the best value by
										renting a ${unit.price} unit
										at <strong>{unit.facilityName}</strong>.
									</Paragraph>
								</Group>
								<Card header={(
									<Link
										secondary
										className="best-facility-title"
										segmentLabel="facility title"
										to={unit.reserveUrl}
									>
										{unit.facilityName}
									</Link>
								)}
								>
									<div className="facility-photo">
										<FacilityImage
											src={unit.facilityImage}
											size="medium"
											lazyLoad
										/>
									</div>
									<div className="unit-details">
										<div>
											<div className="unit-type">
												<UnitIcon
													squareFootage={unit.squareFootage}
												/>
												<span>
													<strong>{unit.sizeText}</strong> Unit
												</span>
											</div>
											<div className="unit-price-rating">
												5/5 Price Rating
											</div>
											{(unit.averagePrice > unit.price) &&
												<div className="unit-price-average">
													{formatUSD(unit.averagePrice - unit.price)} under
													average price
												</div>
											}

										</div>
									</div>
									<div className="unit-price">
										<div className="price">
											${unit.price}
										</div>
										<span className="price-text">
											per month
										</span>
										<Link
											to={unit.reserveUrl}
											segmentLabel="book now button"
											className="reserve-link"
										>
											Book now!
										</Link>
									</div>
								</Card>
							</li>
						))}
					</ul>
				}
			</div>
		</Card>
	);
}

BestPricedUnits.propTypes = {
	location: PropTypes.shape({
		city: PropTypes.string.isRequired,
		state: PropTypes.string.isRequired
	}).isRequired,
	units: PropTypes.arrayOf(
		PropTypes.shape({
			lowestPricedUnit: PropTypes.object
		}),
	)
};

BestPricedUnits.defaultProps = {
	units: []
};

export default enhance(BestPricedUnits);
